import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';

/**
 * AccountRuleOrderDialog — "in what order are these rules evaluated, and where does this one sit?"
 *
 * Account derivation is a two-level rule that is easy to get backwards: **priority decides first**
 * (a lower number is stronger), and the number of filter conditions only breaks ties WITHIN one
 * priority. So a very specific rule loses to a catch-all that was given a lower priority number —
 * invisible in a flat grid, obvious here.
 *
 * Scoped to the rule's own (Legal Entity, Header Type) bucket, because that is the only set it ever
 * competes with. That also keeps the view bounded as a tenant grows to hundreds of rules: you never
 * load more than one bucket, and you open it from the rule you were already looking at.
 *
 * The order is not computed here — the server returns the list the derivation engine itself walks.
 */

interface OrderEntry {
  ruleId: string;
  /** 1-based position; 0 means the rule is inactive and not part of the evaluated set. */
  rank: number;
  priority: number;
  filterKeyCount: number;
  filterJson: string;
  accountId: string;
  accountLabel?: string | null;
  isCatchAll: boolean;
  isMalformed: boolean;
  indistinguishableFrom: string[];
  isSelf: boolean;
}

interface DerivationOrder {
  ruleId: string;
  legalEntityId: string;
  legalEntityCode?: string | null;
  headerType: string;
  entries: OrderEntry[];
  hasAmbiguity: boolean;
}

export interface AccountRuleOrderDialogProps {
  entityId: string;
  open: boolean;
  onClose: () => void;
}

export function AccountRuleOrderDialog({ entityId, open, onClose }: AccountRuleOrderDialogProps) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['account-rule-derivation-order', entityId],
    queryFn: async () =>
      (
        await apiClient.get<DerivationOrder>(
          `/api/finance/accountderivationrule/derivation-order/${entityId}`,
        )
      ).data,
    enabled: open,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('Finance.AccountRuleOrder.Title', { defaultValue: 'Rule evaluation order' })}
      </DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t('common.loading', { defaultValue: 'Loading…' })}
            </Typography>
          </Box>
        )}

        {!!error && (
          <Alert severity="error">
            {t('Finance.AccountRuleOrder.LoadFailed', {
              defaultValue: 'Failed to load the evaluation order.',
            })}
          </Alert>
        )}

        {data && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('Finance.AccountRuleOrder.Scope', {
                defaultValue:
                  'Rules for {{legalEntity}} · {{headerType}} — the only rules this one competes with.',
                legalEntity: data.legalEntityCode ?? '—',
                headerType: t(`Enum:PlanHeaderType:${data.headerType}`, { defaultValue: data.headerType }),
              })}
            </Typography>

            <Alert severity="info" icon={false}>
              {t('Finance.AccountRuleOrder.Explainer', {
                defaultValue:
                  'The first matching rule wins. Priority decides first — a LOWER number is stronger. Only among rules with the same priority does the one with more filter conditions win. A specific rule does not beat a catch-all that was given a lower priority number.',
              })}
            </Alert>

            {data.hasAmbiguity && (
              <Alert severity="warning">
                {t('Finance.AccountRuleOrder.AmbiguityWarning', {
                  defaultValue:
                    'Two or more rules below cannot be told apart (same priority and the same number of conditions). If a plan cell matches both, the account is ambiguous and the line is left pending.',
                })}
              </Alert>
            )}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={48}>
                    {t('Finance.AccountRuleOrder.ColRank', { defaultValue: '#' })}
                  </TableCell>
                  <TableCell>
                    {t('Finance.AccountRuleOrder.ColFilter', { defaultValue: 'Filter' })}
                  </TableCell>
                  <TableCell width={80} align="right">
                    {t('Finance.AccountRuleOrder.ColPriority', { defaultValue: 'Priority' })}
                  </TableCell>
                  <TableCell width={90} align="right">
                    {t('Finance.AccountRuleOrder.ColConditions', { defaultValue: 'Conditions' })}
                  </TableCell>
                  <TableCell>
                    {t('Finance.AccountRuleOrder.ColAccount', { defaultValue: 'Account' })}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.entries.map((e) => (
                  <TableRow
                    key={e.ruleId}
                    selected={e.isSelf}
                    sx={{ '& td': { fontWeight: e.isSelf ? 600 : 400 } }}
                  >
                    <TableCell>
                      {e.rank > 0 ? (
                        e.rank
                      ) : (
                        <Tooltip
                          title={t('Finance.AccountRuleOrder.InactiveHint', {
                            defaultValue: 'Inactive — never evaluated',
                          })}
                        >
                          <span>—</span>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <code>{e.filterJson || '{}'}</code>
                        {e.isCatchAll && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('Finance.AccountRuleOrder.CatchAll', { defaultValue: 'matches everything' })}
                          />
                        )}
                        {e.isMalformed && (
                          <Chip
                            size="small"
                            color="error"
                            label={t('Finance.AccountRuleOrder.Malformed', { defaultValue: 'invalid JSON — never matches' })}
                          />
                        )}
                        {e.indistinguishableFrom.length > 0 && (
                          <Chip
                            size="small"
                            color="warning"
                            // `count` drives i18next's plural resolution (…_one / …_other), so
                            // Swedish reads "1 annan regel" / "2 andra regler" rather than a
                            // slash-form that fits neither.
                            label={t('Finance.AccountRuleOrder.Indistinguishable', {
                              count: e.indistinguishableFrom.length,
                              defaultValue_one: 'ties with {{count}} other rule',
                              defaultValue_other: 'ties with {{count}} other rules',
                            })}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{e.priority}</TableCell>
                    <TableCell align="right">{e.filterKeyCount}</TableCell>
                    <TableCell>{e.accountLabel ?? '—'}</TableCell>
                  </TableRow>
                ))}
                {data.entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        {t('Finance.AccountRuleOrder.Empty', {
                          defaultValue: 'No active rules in this group.',
                        })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close', { defaultValue: 'Close' })}</Button>
      </DialogActions>
    </Dialog>
  );
}
